import { useEffect, useRef, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Send, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { PageHeader, Empty } from "@/components/PageHeader";
import { api, errMsg, fmtDateTime } from "@/lib/api";

const ConvList = ({ items, active, onPick }) => (
  <div className="bg-white border rounded-xl divide-y overflow-hidden h-fit" data-testid="conversazioni-list">
    {items.map((c) => (
      <button key={c.id} onClick={() => onPick(c.id)} data-testid={`conversazione-${c.id}`}
        className={`w-full text-left p-4 transition-colors ${active === c.id ? "bg-emerald-50" : "hover:bg-slate-50"}`}>
        <div className="flex justify-between gap-2">
          <span className="font-medium truncate">{c.interlocutore}</span>
          {c.unread > 0 && <span className="bg-red-600 text-white text-[11px] font-bold rounded-full px-2" data-testid={`conversazione-unread-${c.id}`}>{c.unread}</span>}
        </div>
        <div className="text-xs text-emerald-700 truncate">{c.annuncio_titolo} · {c.ruolo === "venditore" ? "ti ha scritto" : "inserzionista"}</div>
        <div className="text-xs text-muted-foreground truncate mt-1">{c.last_text}</div>
      </button>
    ))}
  </div>
);

const Thread = ({ cid, onSent }) => {
  const [conv, setConv] = useState(null);
  const [testo, setTesto] = useState("");
  const endRef = useRef(null);
  const load = useCallback(() => api.get(`/conversazioni/${cid}`).then((r) => setConv(r.data)), [cid]);

  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: "end" });
  }, [conv?.messaggi?.length]);

  const send = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/conversazioni/${cid}/messaggi`, { testo });
      setTesto("");
      load();
      onSent();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  if (!conv) return null;
  return (
    <div className="bg-white border rounded-xl flex flex-col h-[60vh]" data-testid="conversazione-thread">
      <div className="p-4 border-b">
        <div className="font-heading text-xl uppercase">{conv.interlocutore}</div>
        <div className="text-xs text-muted-foreground">Annuncio: {conv.annuncio_titolo}</div>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50">
        {conv.messaggi.map((m) => (
          <div key={m.id} className={`flex ${m.mio ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[75%] rounded-2xl px-4 py-2 ${m.mio ? "bg-emerald-600 text-white" : "bg-white border"}`} data-testid={`messaggio-${m.id}`}>
              <div className="whitespace-pre-line text-sm">{m.testo}</div>
              <div className={`text-[10px] mt-1 ${m.mio ? "text-emerald-100" : "text-muted-foreground"}`}>{fmtDateTime(m.created_at)}</div>
            </div>
          </div>
        ))}
        <div ref={endRef} />
      </div>
      <form onSubmit={send} className="p-3 border-t flex gap-2">
        <Textarea rows={2} value={testo} onChange={(e) => setTesto(e.target.value)} placeholder="Scrivi un messaggio…" className="resize-none" data-testid="messaggio-input" />
        <Button type="submit" disabled={!testo.trim()} className="bg-emerald-600 hover:bg-emerald-700 self-end" data-testid="messaggio-send-button"><Send className="h-4 w-4" /></Button>
      </form>
    </div>
  );
};

export default function Messaggi() {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState(null);
  const active = params.get("c");
  const load = useCallback(() => api.get("/conversazioni").then((r) => setItems(r.data)), []);
  useEffect(() => {
    load();
  }, [load, active]);

  return (
    <div>
      <PageHeader kicker="Mercatino" title="Messaggi" />
      {items && items.length === 0 && <Empty data-testid="messaggi-empty">Nessun messaggio. Rispondi a un annuncio del Mercatino per iniziare una conversazione.</Empty>}
      {items && items.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6">
          <ConvList items={items.map((c) => (c.id === active ? { ...c, unread: 0 } : c))} active={active} onPick={(id) => setParams({ c: id })} />
          {active ? <Thread key={active} cid={active} onSent={load} /> : (
            <div className="hidden lg:grid place-items-center border border-dashed rounded-xl text-muted-foreground"><span className="flex items-center gap-2"><MessageCircle className="h-5 w-5" />Seleziona una conversazione</span></div>
          )}
        </div>
      )}
    </div>
  );
}
